import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { Dropdown, DropdownButton, SimpleDropdownItem } from "@similarweb/ui-components";
import { Text, Box } from "@similarweb/ui-components/foundations";

import { SetupFieldLoader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/SetupFieldLoader";
import { getDefaultLanguageForCountry } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import type { ILanguage } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

interface ILLMSetupLanguageSelectorProps {
    selectedLanguage: ILanguage;
    onLanguageChange: (language: ILanguage) => void;
    languagesList: ILanguage[];
    isLoading: boolean;
    countryCode?: string;
}

export const LLMSetupLanguageSelector: FC<ILLMSetupLanguageSelectorProps> = ({
    selectedLanguage,
    onLanguageChange,
    languagesList = [],
    isLoading,
    countryCode,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const filteredLanguages = useMemo<ILanguage[]>(() => {
        if (!searchValue) {
            return languagesList;
        }

        return languagesList.filter((lang) => lang.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [languagesList, searchValue]);

    //automatically detect the default language for the selected country
    useEffect(() => {
        if (!countryCode || !languagesList?.length) {
            return;
        }

        const defaultLangId = getDefaultLanguageForCountry(countryCode);
        if (!defaultLangId) {
            return;
        }

        const matchedLang = languagesList.find((lang) => lang.id === defaultLangId);
        if (matchedLang && matchedLang.id !== selectedLanguage.id) {
            onLanguageChange(matchedLang);
        }
    }, [countryCode, languagesList]);

    const handleLanguageSelect = (langId: string): void => {
        const language = languagesList.find((lang) => lang.id === langId);

        if (language) {
            const { name } = language;

            allTrackers.dispatchTrackEvent("Dropdown", "Select", `Ai Brand Visibility/Language/${name}`);
            onLanguageChange(language);
            setSearchValue("");
        }
    };

    const onToggle = (state: boolean): void => {
        if (state) {
            allTrackers.dispatchTrackEvent("Dropdown", "Open", `Ai Brand Visibility/Language`);
        }

        setIsOpen(state);
    };

    return (
        <Box
            position="relative"
            borderRadius="3px"
            width="232px"
            height="40px"
        >
            {isLoading ? (
                <SetupFieldLoader />
            ) : (
                <Dropdown
                    isOpen={isOpen}
                    onToggle={onToggle}
                    onClick={({ id }) => handleLanguageSelect(id)}
                    onSearch={(term) => setSearchValue(term)}
                    closeOnItemClick
                    appendTo="body"
                    hasSearch
                >
                    {[
                        <DropdownButton key="language-dropdown-button">
                            <Text
                                fontVariant="chip1"
                                data-automation="selected-lang"
                            >
                                {selectedLanguage.name}
                            </Text>
                        </DropdownButton>,
                        ...filteredLanguages.map(({ id, name }) => (
                            <SimpleDropdownItem
                                key={id}
                                id={id}
                            >
                                {name}
                            </SimpleDropdownItem>
                        )),
                    ]}
                </Dropdown>
            )}
        </Box>
    );
};
